import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { activeSlots, isPack, optionLines, slotLines } from "@/lib/catalog";
import { formatPrice } from "@/lib/format";
import { LeadTimeNote } from "@/components/shop/LeadTimeNote";
import { AgendaNote } from "@/components/agenda/AgendaNote";
import { refreshAgenda, useOrderGate } from "@/lib/agendaStore";
import { leadTimeSeen, patternExtraSeen, refreshLeadTime, useLeadTime } from "@/lib/leadTime";
import { combineExtras } from "@/lib/patternAvailability";
import { FREE_SHIPPING_EUR, postJson } from "@/lib/publicApi";
import { usePageMeta } from "@/lib/seo";
import { GiftBoxes } from "@/components/checkout/GiftBoxes";
import { fitGift, giftFee, giftPayload, giftProblem } from "@/lib/gift";
import { CouponBox } from "@/components/checkout/CouponBox";
import { MessageOrder } from "@/components/shop/MessageOrder";
import { AddressFields, ContactFields, ShippingOptions } from "@/components/checkout/CheckoutFields";

const COUPON_KEY = "babybib-coupon";
const EMPTY = { name: "", email: "", phone: "", nif: "", address_line1: "", address_line2: "", postal_code: "", city: "", country: "PT", notes: "", baby_name: "" };

const Review = ({ items, subtotal, shipping, coupon, giftCount = 1, boxFee = 0 }) => (
  <div className="space-y-3" data-testid="checkout-review">
    {items.map((i) => (
      <div key={i.key} className="flex justify-between gap-4 text-sm">
        <div>
          <p className="font-medium text-ink">{i.qty} x {i.product.name}</p>
          {!isPack(i.product) && <p className="text-xs text-taupe-strong">{activeSlots(i.product, i.options).some((s) => s.onlyWith) ? "Frente" : "Padrão"}: {i.pattern?.name}{i.personalization && `. Nome: ${i.personalization}`}</p>}
          {slotLines(i.product, i.slots).map((s) => <p key={s.label} className="text-xs text-taupe-strong">{s.label}: {[s.model, s.pattern?.name].filter(Boolean).join(", ")}</p>)}
          {i.note && <p className="text-xs text-taupe-strong" data-testid="checkout-note">Comentário: <span className="font-medium italic text-ink">{i.note}</span></p>}
          {i.phraseText && <p className="text-xs text-taupe-strong" data-testid="checkout-phrase">Texto do saquinho: <span className="font-medium text-ink">{i.phraseText}</span></p>}
          {optionLines(i.product, i.options).map((o) => <p key={o.label} className="text-xs text-taupe-strong">{o.label}: {o.value}</p>)}
          {i.babyCm && <p className="text-xs text-taupe-strong">Medida do bebé: {i.babyCm} cm</p>}
        </div>
        <p className="whitespace-nowrap">{formatPrice(i.total)}</p>
      </div>
    ))}
    {coupon?.gift && <p className="text-sm font-medium text-bordeaux" data-testid="checkout-gift">{giftCount} x {coupon.gift.name} <span className="font-normal">(oferta)</span></p>}
    <div className="space-y-2 border-t border-ink/10 pt-3 text-sm">
      <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span></div>
      {coupon?.discount_cents > 0 && <div className="flex justify-between text-bordeaux"><span>Desconto ({coupon.code})</span><span data-testid="checkout-discount">−{formatPrice(coupon.discount_cents / 100)}</span></div>}
      {boxFee > 0 && <div className="flex justify-between"><span>Caixas de oferta adicionais</span><span data-testid="checkout-gift-fee">{formatPrice(boxFee)}</span></div>}
      <div className="flex justify-between"><span>Portes</span><span data-testid="checkout-shipping">{shipping ? formatPrice(shipping.price) : "Escolhe uma opção"}</span></div>
      <div className="flex justify-between border-t border-ink/10 pt-3 text-base font-semibold"><span>Total</span><span data-testid="checkout-total">{formatPrice(subtotal - (coupon?.discount_cents ?? 0) / 100 + boxFee + (shipping?.price ?? 0))}</span></div>
    </div>
  </div>
);

export default function CheckoutPage() {
  usePageMeta("Finalizar compra", "Finalizar compra na Babybib.");
  const { items, subtotal, hasUnpriced, hasSoldOut, hasPhraseProblem, patterns } = useCart();
  const leadTime = useLeadTime();
  const { blocked } = useOrderGate(items.map((i) => i.slug));
  const [customer, setCustomer] = useState(EMPTY);
  const [zone, setZone] = useState("pt-continental");
  const [methods, setMethods] = useState(null);
  const [quoteFailed, setQuoteFailed] = useState(false);
  const [quoteTry, setQuoteTry] = useState(0);
  const [methodId, setMethodId] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cartKey = items.map((i) => `${i.slug}:${i.qty}`).join(",");
  const [coupon, setCoupon] = useState(null);
  const [couponExtra, setCouponExtra] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [gift, setGift] = useState(null);
  const toItems = () => items.map((i) => ({ slug: i.slug, pattern_id: i.patternId, personalization: i.personalization, personalization_zone: i.zone ?? null, phrase: i.phrase ? { option_id: i.phrase.optionId, article: i.phrase.articleId ?? null, name: i.phrase.name } : null, baby_cm: i.babyCm ?? null, note: i.note ?? null, slots: Object.entries(i.slots ?? {}).map(([slot_id, v]) => ({ slot_id, model_id: v.modelId ?? null, pattern_id: v.patternId })), options: Object.entries(i.options ?? {}).map(([option_id, v]) => ({ option_id, choice_id: v.choiceId, text: v.text || null })), qty: i.qty }));
  const itemsKey = JSON.stringify(toItems());
  const applyCoupon = async (raw, silent = false) => {
    setCouponBusy(true);
    setCouponError("");
    try {
      const info = await postJson("/coupons/check", { code: raw.trim(), items: toItems() });
      setCoupon(info);
      try { localStorage.removeItem(COUPON_KEY); } catch { /* sem armazenamento */ }
    } catch (err) {
      setCoupon(null);
      if (!silent) setCouponError(err.message);
    } finally {
      setCouponBusy(false);
    }
  };
  // Um código guardado numa campanha da página inicial aplica-se sozinho; se o carrinho mudar, o código volta a ser verificado.
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(COUPON_KEY); } catch { /* sem armazenamento */ }
    if (saved && items.length) applyCoupon(saved, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Se o carrinho mudar (quantidades), as caixas de oferta acertam-se sozinhas.
  useEffect(() => { setGift((g) => fitGift(g, items)); }, [itemsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (coupon) applyCoupon(coupon.code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  useEffect(() => {
    if (!cartKey) return undefined;
    let alive = true;
    setMethods(null);
    setQuoteFailed(false);
    const body = { zone, items: cartKey.split(",").map((s) => ({ slug: s.split(":")[0], qty: Number(s.split(":")[1]) })) };
    // Um pedido que fica pendurado não pode deixar "A calcular..." para sempre: tenta até 3 vezes, 8 s cada.
    const quote = async () => {
      for (let n = 0; n < 3 && alive; n++) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 8000);
        try {
          return await postJson("/shipping/quote", body, ctrl.signal);
        } catch (e) {
          if (n === 2) throw e;
        } finally {
          clearTimeout(timer);
        }
      }
      return [];
    };
    quote()
      .then((list) => {
        if (!alive) return;
        const arr = Array.isArray(list) ? list : [];
        setMethods(arr);
        setMethodId((cur) => (arr.some((m) => m.id === cur) ? cur : arr[0]?.id ?? ""));
      })
      .catch(() => { if (alive) { setQuoteFailed(true); setMethods([]); } });
    return () => {
      alive = false;
    };
  }, [zone, cartKey, quoteTry]);

  if (items.length === 0 || hasUnpriced || hasSoldOut || hasPhraseProblem) return <Navigate to="/carrinho" replace />;
  // Portes grátis a partir de 100 € em peças, já com o desconto (o servidor volta a calcular).
  const goods = subtotal - (coupon?.discount_cents ?? 0) / 100;
  const freeShip = goods >= FREE_SHIPPING_EUR;
  const picked = methods?.find((m) => m.id === methodId);
  const shipping = picked && freeShip ? { ...picked, price: 0 } : picked;
  // Com caixas de oferta, o código de oferta dá uma oferta por caixa (por mamã, se o código pede o nome da mamã).
  const perBox = coupon?.kind === "gift" && !!gift;
  const momBoxes = gift ? gift.boxes.filter((b) => b.mom.trim()).length : 0;
  const giftCount = perBox ? (coupon.extra_field ? momBoxes : gift.boxes.length) : 1;
  const couponGiftProblem = perBox && coupon.extra_field && momBoxes === 0 ? `Para receberes a oferta, indica o nome da mamã (${coupon.extra_field.label.toLowerCase()}) em pelo menos uma caixa.` : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4}-\d{3}$/.test(customer.postal_code.trim())) return setError("Código postal inválido. Usa o formato 1234-567 (só fazemos envios para Portugal).");
    setBusy(true);
    try {
      const { baby_name: babyName, ...rest } = customer;
      const clean = Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, typeof v === "string" ? v.trim() || null : v]));
      const data = await postJson("/checkout", {
        items: toItems(), gift: giftPayload(gift), coupon: coupon ? { code: coupon.code, extra: coupon.extra_field && !perBox ? couponExtra.trim() : null } : null,
        lead_time_seen: leadTimeSeen(leadTime), pattern_extra_seen: patternExtraSeen(combineExtras(patterns)), zone, shipping_method_id: methodId, baby_name: babyName.trim() || null, customer: { ...clean, country: "PT" }, accept_terms: terms, origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (err) {
      setError(err.message);
      refreshLeadTime();
      refreshAgenda();
      setBusy(false);
    }
  };

  return (
    <div className="bb-container pb-10 pt-12 sm:pt-16">
      <p className="bb-eyebrow mb-4">Finalizar compra</p>
      <h1 className="font-serif text-[2.6rem] font-normal leading-none tracking-tight sm:text-6xl">Dados de envio</h1>
      <form onSubmit={submit} noValidate className="mt-8 grid grid-cols-1 gap-10 sm:mt-10 lg:grid-cols-12 lg:gap-16" data-testid="checkout-form">
        <div className="space-y-10 lg:col-span-7">
          <ContactFields value={customer} onChange={setCustomer} />
          <AddressFields value={customer} onChange={setCustomer} zone={zone} onZone={setZone} />
          <ShippingOptions methods={methods} value={methodId} onChange={setMethodId} failed={quoteFailed} onRetry={() => setQuoteTry((n) => n + 1)} free={freeShip} missing={FREE_SHIPPING_EUR - goods} />
        </div>
        <aside className="h-fit space-y-5 rounded-3xl bg-linen p-6 sm:p-8 lg:sticky lg:top-24 lg:col-span-5">
          <h2 className="font-serif text-2xl">Revê a encomenda</h2>
          <CouponBox perBox={perBox} coupon={coupon} extra={couponExtra} onExtra={setCouponExtra} onApply={applyCoupon} onRemove={() => { setCoupon(null); setCouponExtra(""); }} error={couponError} busy={couponBusy} />
          <GiftBoxes items={items} value={gift} onChange={setGift} problem={giftProblem(gift) ?? couponGiftProblem} />
          <Review items={items} subtotal={subtotal} shipping={shipping} coupon={coupon} giftCount={giftCount} boxFee={giftFee(gift)} />
          <LeadTimeNote variant="short" className="!bg-ivory" patterns={patterns} />
          <AgendaNote slugs={items.map((i) => i.slug)} />
          <label className="flex items-start gap-3 text-sm" htmlFor="terms">
            <input id="terms" type="checkbox" data-testid="checkout-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 h-5 w-5 accent-[rgb(var(--bb-bordeaux))]" />
            <span>Li e aceito os <Link to="/termos" className="underline">termos</Link> e a <Link to="/privacidade" className="underline">política de privacidade</Link>.</span>
          </label>
          {error && <p role="alert" data-testid="checkout-error" className="rounded-xl bg-rose-soft px-4 py-3 text-sm text-bordeaux">{error}</p>}
          <button type="submit" data-testid="checkout-pay-button" disabled={busy || blocked || !terms || !shipping || !!giftProblem(gift) || !!couponGiftProblem || (!!coupon?.extra_field && !perBox && !couponExtra.trim())} className="bb-btn-primary w-full"><Lock className="h-4 w-4" /> {busy ? "A abrir pagamento..." : `Pagar ${formatPrice(subtotal - (coupon?.discount_cents ?? 0) / 100 + giftFee(gift) + (shipping?.price ?? 0))}`}</button>
          <MessageOrder testId="checkout-message-order" message="Olá Alexandra! Gostava de fazer uma encomenda por mensagem 😊" />
          <p className="text-xs leading-relaxed text-taupe-strong">O pagamento é feito na página segura da Stripe. Não vejo nem guardo dados do cartão.</p>
        </aside>
      </form>
    </div>
  );
}
